import { useState } from 'react'
import { FileDown } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FilterBar, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { downloadFile } from '@/lib/api/client'
import { useIssuedLetters, useRequestContext, useRequestTypes, useRequests } from '../api'
import { displayValue, REQUEST_STATUS_TONE, type RequestStatus } from '../types'
import { Select } from '@/components/ui/select'

const STATUSES: (RequestStatus | '')[] = ['', 'PENDING', 'APPROVED', 'REJECTED', 'RETURNED', 'CANCELLED']

/**
 * Every request, and one of them in full.
 *
 * <p>The detail is one call — the form paired with the answers, the attachments, and whether
 * a letter came out of it. Rendering the payload without the labels the administrator gave
 * each field would show a bag of keys, which is not something anybody can act on.
 */
export function RequestsPage() {
  useBreadcrumb()
  const types = useRequestTypes()
  const [typeId, setTypeId] = useState('')
  const [status, setStatus] = useState<RequestStatus | ''>('')
  const [selected, setSelected] = useState<string | null>(null)

  const requests = useRequests(typeId || undefined, status || undefined)
  const context = useRequestContext(selected)
  const letters = useIssuedLetters(context.data?.request.employeeId)

  const letter = context.data?.request.generatedLetterId
    ? letters.data?.find((row) => row.id === context.data?.request.generatedLetterId)
    : undefined

  return (
    <div className="space-y-6">
      <PageHeader
        title="Requests"
        subtitle="Everything raised, of every kind this company has defined."
      />

      <FilterBar>
        <Select
          aria-label="Kind"
          id="filter-type" value={typeId}
          onChange={(event) => setTypeId(event.target.value)}
          className="min-w-48"
        >
          <option value="">Every kind</option>
          {(types.data ?? []).map((type) => (
            <option key={type.id} value={type.id}>{type.name}</option>
          ))}
        </Select>

        <Select
          aria-label="Status"
          id="filter-status" value={status}
          onChange={(event) => setStatus(event.target.value as RequestStatus | '')}
          className="min-w-40"
        >
          {STATUSES.map((option) => (
            <option key={option} value={option}>{option === '' ? 'Any status' : option}</option>
          ))}
        </Select>
      </FilterBar>

      <div className="grid gap-4 xl:grid-cols-12">
        <div className="xl:col-span-6">
          <Card>
            {requests.isPending && <Skeleton className="m-4 h-32" />}
            {requests.isError && (
              <ErrorState error={requests.error} onRetry={() => void requests.refetch()} />
            )}
            {requests.isSuccess && requests.data.length === 0 && (
              <EmptyState title="Nothing matches" description="No request of that kind and status." />
            )}
            {(requests.data?.length ?? 0) > 0 && (
              <ul className="divide-y divide-[var(--color-border)]">
                {requests.data?.map((request) => (
                  <li key={request.id}>
                    <button
                      type="button"
                      aria-current={selected === request.id ? 'true' : undefined}
                      onClick={() => setSelected(request.id)}
                      className={
                        'flex w-full flex-col items-start gap-1 p-3 text-start text-sm hover:bg-[var(--color-page)]'
                        + (selected === request.id ? ' bg-[var(--color-primary-soft)]' : '')
                      }
                    >
                      <span className="font-medium text-[var(--color-heading)]">
                        {request.subject ?? request.requestTypeName}
                      </span>
                      <span className="flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted)]">
                        <code>{request.requestNo}</code>
                        <StatusBadge status={request.status} tone={REQUEST_STATUS_TONE[request.status]} />
                        {request.appliedAt && <DateValue value={request.appliedAt} />}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="xl:col-span-6">
          {selected == null ? (
            <Card className="p-6">
              <EmptyState title="Pick a request" description="Its form and answers appear here." />
            </Card>
          ) : context.isPending ? (
            <Skeleton className="h-64 w-full rounded-[var(--radius-card)]" />
          ) : context.data ? (
            <Card className="space-y-4 p-4">
              <div>
                <h2 className="text-sm font-semibold text-[var(--color-heading)]">
                  {context.data.request.subject}
                </h2>
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  {context.data.employeeName} · {context.data.requestTypeName}
                  {context.data.slaHours != null && ` · answered within ${context.data.slaHours} h`}
                </p>
              </div>

              <dl className="space-y-2 text-sm">
                {context.data.answers.map((answer) => (
                  <div key={answer.code} className="flex flex-wrap items-baseline gap-2">
                    <dt className="min-w-40 text-[var(--color-muted)]">
                      {answer.label}
                      {answer.withdrawn && (
                        <span className="ms-1.5 text-xs">(withdrawn field)</span>
                      )}
                    </dt>
                    <dd className="font-medium text-[var(--color-heading)]">
                      {displayValue(answer.value)}
                    </dd>
                  </div>
                ))}
              </dl>

              {context.data.request.decisionComment && (
                <p className="rounded-[var(--radius-card)] bg-[var(--color-page)] p-3 text-sm">
                  <span className="text-[var(--color-muted)]">Decision: </span>
                  {context.data.request.decisionComment}
                </p>
              )}

              {letter && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] bg-[var(--color-tint-green)] p-3">
                  <span className="text-sm text-[var(--color-success)]">
                    Letter {letter.referenceNo} was issued on{' '}
                    <DateValue value={letter.renderedAt} />
                  </span>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() =>
                      void downloadFile(
                        `/v1/files/${letter.storedFileId}`,
                        `${letter.referenceNo ?? 'letter'}.pdf`,
                      )
                    }
                  >
                    <FileDown className="size-4" aria-hidden="true" />
                    Download the PDF
                  </Button>
                </div>
              )}
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  )
}
